import React, { useRef } from 'react';
import { X, Paperclip, Loader2 } from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '../../ui/dialog';
import { Input } from '../../ui/input';
import { Button } from '../../ui/button';
import { Progress } from '../../ui/progress';
import CrmMailRecipientInput from './CrmMailRecipientInput';
import CrmMailRichEditor from './CrmMailRichEditor';
import AsyncSubmitButton from '../../ui/AsyncSubmitButton';

export default function CrmMailCompose({
  open,
  onClose,
  compose,
  setCompose,
  mode,
  sending,
  onSend,
  onSearchRecipients,
  recipients,
  onUpload,
  uploadProgress,
}) {
  const fileRef = useRef(null);
  const toRef = useRef(null);
  const ccRef = useRef(null);
  const bccRef = useRef(null);

  const handleSend = async () => {
    const flushed = {
      ...compose,
      to: toRef.current?.commitPending() ?? compose.to,
      cc: compose.showCc ? (ccRef.current?.commitPending() ?? compose.cc) : compose.cc,
      bcc: compose.showBcc ? (bccRef.current?.commitPending() ?? compose.bcc) : compose.bcc,
    };
    await onSend(flushed);
  };

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      await onUpload(file);
    } catch { /* toast handled by parent */ }
    e.target.value = '';
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {mode === 'reply' ? 'Reply' : mode === 'reply_all' ? 'Reply all' : mode === 'forward' ? 'Forward' : 'New message'}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3">
          <CrmMailRecipientInput
            ref={toRef}
            label="To"
            value={compose.to}
            onChange={(to) => setCompose({ ...compose, to })}
            onSearch={onSearchRecipients}
            suggestions={recipients}
          />

          <div className="flex gap-2 text-xs">
            {!compose.showCc && (
              <button type="button" className="text-blue-600 hover:underline" onClick={() => setCompose({ ...compose, showCc: true })}>Cc</button>
            )}
            {!compose.showBcc && (
              <button type="button" className="text-blue-600 hover:underline" onClick={() => setCompose({ ...compose, showBcc: true })}>Bcc</button>
            )}
          </div>

          {compose.showCc && (
            <CrmMailRecipientInput
              ref={ccRef}
              label="Cc"
              value={compose.cc}
              onChange={(cc) => setCompose({ ...compose, cc })}
              onSearch={onSearchRecipients}
              suggestions={recipients}
            />
          )}
          {compose.showBcc && (
            <CrmMailRecipientInput
              ref={bccRef}
              label="Bcc"
              value={compose.bcc}
              onChange={(bcc) => setCompose({ ...compose, bcc })}
              onSearch={onSearchRecipients}
              suggestions={recipients}
            />
          )}

          <Input
            placeholder="Subject"
            value={compose.subject}
            onChange={(e) => setCompose({ ...compose, subject: e.target.value })}
          />

          <CrmMailRichEditor
            value={compose.body_html}
            onChange={(body_html) => setCompose({ ...compose, body_html })}
          />

          <div className="flex items-center gap-2">
            <input
              ref={fileRef}
              type="file"
              className="hidden"
              accept=".png,.jpg,.jpeg,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.zip"
              onChange={handleFile}
            />
            <Button type="button" variant="outline" size="sm" onClick={() => fileRef.current?.click()}>
              <Paperclip className="w-4 h-4 mr-1" /> Attach
            </Button>
            {(compose.attachment_tokens?.length > 0) && (
              <span className="text-xs text-muted-foreground">
                {compose.attachment_tokens.length} attachment(s)
              </span>
            )}
          </div>
          {uploadProgress != null && (
            <Progress value={uploadProgress} className="h-1" />
          )}

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <AsyncSubmitButton
              onClick={handleSend}
              loading={sending}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              Send
            </AsyncSubmitButton>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
